import React, {useCallback, useState} from "react";
import {Divider, Form, message} from "antd";
import {FormInputV2} from "../../../../components/form/index.jsx";
import {AvailabilityHourly, StyledButton} from "../../../../components";
import {AutoDirectionProvider} from "../../../../AutoDirectionProvider.jsx";
import {SingleFileUploadHelper} from "../../../upload/AdminUploadHelper.jsx";
import "./upload-delivery-provider-form.css";
import {DeliveryProviderPricingForm} from "./DeliveryProviderPricingForm.jsx";
import {carType, DeliveryProviderFormFieldsValidationRules} from "./consts.jsx";
import {StepperDropdownInput} from "../../../../wizards/components/StepperDropdownInput.jsx";
import {getDealerTypeReversed, ProviderNetPaymentTermsTypes} from "../../../upload/AdminUploadUtils.jsx";
import {DealerTypeKinds} from "../../../../wizards/wizardConsts.jsx";
import {get} from "lodash";
import {StringBuilder} from "../../../../AppUtils.js";
import {DeliverySystem} from "../../consts.js";

export const DeliveryProviderForm = ({formValues, setFormValues, onSave}) => {
    const [isSavingProviderLoading, setIsSavingProviderLoading] = useState(false);

    const validateFormFields = useCallback(() => {
        for (const [field, validator] of Object.entries(DeliveryProviderFormFieldsValidationRules)) {
            const formValueByKey = get(formValues, field);
            const error = validator(formValueByKey);
            setFormValues(`errors.${field}`, error ?? null);
        }

        if (formValues?.errors) {
            for (const [field, errorMessage] of Object.entries(formValues.errors ?? {})) {
                if (errorMessage) {
                    const error = new StringBuilder("שגיאה!").append(`${field}:`).append(errorMessage).toString();
                    message.error(error);
                    return false;
                }
            }
        }

        return true;
    }, [formValues, setFormValues]);

    const onFinish = useCallback(async () => {
        const isValid = validateFormFields();
        if (!isValid) {
            return;
        }
        setIsSavingProviderLoading(true);
        await onSave();
        setIsSavingProviderLoading(false);
    }, [onSave, validateFormFields]);

    return (
        <div className="upload-delivery-provider-form-container">
            <Form layout="vertical" className="form-container">
                <span className="form-title">פרטים על חברת השילוח</span>
                <div className="form-inputs-container">
                    <Form.Item
                        label="שם חברת השילוח"
                        validateStatus={formValues?.errors?.name ? "error" : ""}
                        help={formValues?.errors?.name}>
                        <FormInputV2
                            className="form-input"
                            value={formValues?.name}
                            onChange={e =>
                                setFormValues("name", e.target.value, DeliveryProviderFormFieldsValidationRules.name)
                            }
                        />
                    </Form.Item>

                    <Form.Item label="סוג עוסק">
                        <StepperDropdownInput
                            className="form-input"
                            style={{width: "380px", height: "40px"}}
                            arrowStyle={{position: "absolute", right: "", left: 10}}
                            placeholder="סוג עוסק"
                            value={getDealerTypeReversed(formValues?.dealerType)}
                            items={Object.values(DealerTypeKinds).map((val, index) => ({
                                key: val,
                                label: getDealerTypeReversed(val),
                                className: `dealer-type-${index}`
                            }))}
                            onChange={val => setFormValues("dealerType", val)}
                        />
                    </Form.Item>

                    <Form.Item
                        validateStatus={formValues?.errors?.paymentTerms ? "error" : ""}
                        help={formValues?.errors?.paymentTerms}
                        label="תנאי תשלום - שוטף">
                        <StepperDropdownInput
                            className="form-input"
                            style={{width: "380px", height: "40px"}}
                            arrowStyle={{position: "absolute", right: "", left: 10}}
                            value={formValues?.paymentTerms}
                            placeholder={"תנאי שוטף"}
                            items={Object.values(ProviderNetPaymentTermsTypes).map((val, index) => ({
                                key: val,
                                label: val,
                                className: `net-payment-terms-option-${index}`
                            }))}
                            onChange={val =>
                                setFormValues(
                                    "paymentTerms",
                                    parseInt(val),
                                    DeliveryProviderFormFieldsValidationRules.paymentTerms
                                )
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        label="איש קשר"
                        validateStatus={formValues?.errors?.contactName ? "error" : ""}
                        help={formValues?.errors?.contactName}>
                        <FormInputV2
                            className="form-input"
                            value={formValues?.contactName}
                            onChange={e =>
                                setFormValues(
                                    "contactName",
                                    e.target.value,
                                    DeliveryProviderFormFieldsValidationRules.contactName
                                )
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        label="טלפון"
                        validateStatus={formValues?.errors?.phone ? "error" : ""}
                        help={formValues?.errors?.phone}>
                        <FormInputV2
                            className="form-input"
                            value={formValues?.phone}
                            onChange={e =>
                                setFormValues("phone", e.target.value, DeliveryProviderFormFieldsValidationRules.phone)
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        label="מייל התחברות"
                        validateStatus={formValues?.errors?.email ? "error" : ""}
                        help={formValues?.errors?.email}>
                        <FormInputV2
                            className="form-input"
                            value={formValues?.email}
                            onChange={e =>
                                setFormValues("email", e.target.value, DeliveryProviderFormFieldsValidationRules.email)
                            }
                        />
                    </Form.Item>

                    <AutoDirectionProvider>
                        <Form.Item className="availability" label="שעות פעילות בית העסק">
                            <AvailabilityHourly
                                value={formValues?.availability}
                                onChange={values => setFormValues("availability", values)}
                            />
                        </Form.Item>
                    </AutoDirectionProvider>

                    <Form.Item label="הסכם עבודה">
                        <SingleFileUploadHelper
                            openFile={() => window.open(formValues?.businessCertificate?.imageUrl, "Image")}
                            uploadText="הסכם עבודה"
                            fileName={formValues?.businessCertificate?.name}
                            onFileChange={(imageUrl, imageId, name) =>
                                setFormValues("businessCertificate", {
                                    imageUrl: `https://res.cloudinary.com/well-b-prod/image/upload/${imageId}`,
                                    imageId: imageId,
                                    name: name
                                })
                            }
                        />
                    </Form.Item>

                    <Form.Item label="מערכת שילוחים">
                        <StepperDropdownInput
                            className="form-input"
                            style={{width: "380px", height: "40px"}}
                            arrowStyle={{position: "absolute", right: "", left: 10}}
                            placeholder="מערכת שילוחים"
                            value={formValues?.deliverySystem}
                            items={Object.values(DeliverySystem).map(val => ({
                                key: val,
                                label: val
                            }))}
                            onChange={val => setFormValues("deliverySystem", val)}
                        />
                    </Form.Item>
                    <Form.Item
                        label="מספר תעודת זהות"
                        validateStatus={formValues?.errors?.idNumber ? "error" : ""}
                        help={formValues?.errors?.idNumber}>
                        <FormInputV2
                            className="form-input"
                            value={formValues?.idNumber}
                            onChange={e =>
                                setFormValues(
                                    "idNumber",
                                    e.target.value,
                                    DeliveryProviderFormFieldsValidationRules.idNumber
                                )
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        label="מספר רכב"
                        validateStatus={formValues?.errors?.licenseNumber ? "error" : ""}
                        help={formValues?.errors?.licenseNumber}>
                        <FormInputV2
                            className="form-input"
                            value={formValues?.licenseNumber}
                            onChange={e =>
                                setFormValues(
                                    "licenseNumber",
                                    e.target.value,
                                    DeliveryProviderFormFieldsValidationRules.licenseNumber
                                )
                            }
                        />
                    </Form.Item>
                </div>

                <Divider />
                <span className="form-title">מחיר משלוח</span>
                <div className="form-sub-title">חישוב עלות משלוח עבור סוג ההזמנה</div>
                <div className="delivery-provider-pricing-container">
                    <DeliveryProviderPricingForm
                        title="מודל תמחור"
                        onChange={setFormValues}
                        formValues={{
                            [carType.car]: {
                                ...(formValues?.deliveryPricingSetting ?? {}),
                                homeDeliveryPrice: formValues?.homeDeliveryPrice
                            }
                        }}
                    />
                </div>

                <StyledButton
                    className="upload-delivery-provider-button"
                    loading={isSavingProviderLoading}
                    onClick={onFinish}>
                    שמירה
                </StyledButton>
            </Form>
        </div>
    );
};
